import { isPlatformBrowser } from '@angular/common';
import { HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import {
  AUTH_ENTRA_LOGIN_ENDPOINT,
  AUTH_GET_SCOPES_ENDPOINT,
  AUTH_LOGIN_ENDPOINT,
  AUTH_REFRESH_TOKEN_ENDPOINT,
  AUTH_CHANGE_PASSWORD_ON_FIRST_LOGIN_ENDPOINT,
  CONTRACT_BY_NIPT_ENDPOINT,
  SUBSCRIPTION_EXTERNAL_PROVIDER_INITIALIZE_ENDPOINT,
  SUBSCRIPTION_EXTERNAL_PROVIDER_REGISTER_FIRST_ACTIVITY_ENDPOINT,
  SUBSCRIPTION_CREATE_WITH_PAYMENT_ENDPOINT,
  SUBSCRIPTION_UPDATE_AUTORENEW_ENDPOINT,
  PAYMENTS_TWOCHECKOUT_INITIATE_PATH,
  PAYMENTS_TWOCHECKOUT_STATUS_PATH_PREFIX,
  SUBSCRIPTION_CREATE_PENDING_FOR_QR_ENDPOINT,
  SUBSCRIPTION_CONFIRM_QR_PAYMENT_ENDPOINT,
  SUBSCRIPTION_CHECK_QR_PAYMENT_STATUS_ENDPOINT,
  SUBSCRIPTION_CONFIRM_EPOSTA_REDIRECT_PAYMENT_ENDPOINT,
  SUBSCRIPTION_FAIL_EPOSTA_REDIRECT_PAYMENT_ENDPOINT,
  CONTRACT_GENERATE_ENDPOINT,
} from '@core/constant/endpoints';
import { LocalStorageService } from '@core/services/local-storage.service';
import { AuthActions } from '@core/store/auth/auth.actions';
import { selectAuth } from '@core/store/auth/auth.selector';
import { isTokenValid } from '@core/utils/auth';
import { Store } from '@ngrx/store';
import { EMPTY, skip, switchMap, take, takeWhile } from 'rxjs';
import { LOCAL_STORAGE_KEY_AUTH_TOKEN, LOCAL_STORAGE_KEY_USER_NAME } from '@core/constant/constant';

/**
 * Interceptor that adds an Authorization header to requests that are authenticated and target the API URL.
 *
 * @param request The request object.
 * @param next The next interceptor in the chain.
 *
 * @returns The next Observable.
 */
export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(Store);
  const localStorageService = inject(LocalStorageService);
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) {
    return EMPTY;
  }
  const credential = localStorageService.getAuthToken();
  // Do not add Bearer token: login/refresh, or endpoints that use ExternalSession auth (pricing/SessionId flow).
  const skipBearerTokenUrls = [
    AUTH_LOGIN_ENDPOINT,
    AUTH_REFRESH_TOKEN_ENDPOINT,
    AUTH_ENTRA_LOGIN_ENDPOINT,
    AUTH_CHANGE_PASSWORD_ON_FIRST_LOGIN_ENDPOINT,
    SUBSCRIPTION_EXTERNAL_PROVIDER_INITIALIZE_ENDPOINT,
    SUBSCRIPTION_EXTERNAL_PROVIDER_REGISTER_FIRST_ACTIVITY_ENDPOINT,
    SUBSCRIPTION_CREATE_WITH_PAYMENT_ENDPOINT,
    SUBSCRIPTION_UPDATE_AUTORENEW_ENDPOINT,
    SUBSCRIPTION_CREATE_PENDING_FOR_QR_ENDPOINT,
    SUBSCRIPTION_CONFIRM_QR_PAYMENT_ENDPOINT,
    SUBSCRIPTION_CHECK_QR_PAYMENT_STATUS_ENDPOINT,
    SUBSCRIPTION_CONFIRM_EPOSTA_REDIRECT_PAYMENT_ENDPOINT,
    SUBSCRIPTION_FAIL_EPOSTA_REDIRECT_PAYMENT_ENDPOINT,
    CONTRACT_GENERATE_ENDPOINT,
    CONTRACT_BY_NIPT_ENDPOINT,
  ];
  const skipExternalSessionPayment =
    req.url.includes(PAYMENTS_TWOCHECKOUT_INITIATE_PATH) ||
    req.url.includes(PAYMENTS_TWOCHECKOUT_STATUS_PATH_PREFIX);
  if (
    skipBearerTokenUrls.some((url) => req.url.indexOf(url) > 0) ||
    skipExternalSessionPayment
  ) {
    return next(req);
  }

  if (req.url.includes('assets/i18n/')) {
    return next(req);
    }

  if (!credential) {
    return EMPTY;
  }

  if (isTokenValid(credential)) {
    return next(
      req.clone({
        setHeaders: {
          Authorization: `Bearer ${credential.accessToken}`,
        },
      })
    );
  }

  if (credential.refreshToken) {
      store.dispatch(AuthActions.refreshToken());
      return store.select(selectAuth).pipe(
        skip(1),
        takeWhile(
          (authDataRefresh) => !!authDataRefresh.credential?.accessToken
        ),
        take(1),
      switchMap((authDataRefresh) => {
        if (authDataRefresh.credential?.accessToken) {
          return next(
            req.clone({
              setHeaders: {
                Authorization: `Bearer ${authDataRefresh.credential.accessToken}`,
              },
            })
      );
    }
        localStorageService.removeData(LOCAL_STORAGE_KEY_AUTH_TOKEN);
        localStorageService.removeData(LOCAL_STORAGE_KEY_USER_NAME);
    return EMPTY;
      })
    );
  }

  localStorageService.removeData(LOCAL_STORAGE_KEY_AUTH_TOKEN);
  localStorageService.removeData(LOCAL_STORAGE_KEY_USER_NAME);
  return EMPTY;
};
